'use client';

import type { FieldErrors } from 'react-hook-form';
import type { GroupBase, OptionProps, SingleValueProps } from 'react-select';
import type { SingleValue } from 'react-select';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import type { ReminderDayType } from '@/types/clientTasksApiTypes';
import type { Nullable } from '@/types/common';
import { cn } from '@/utils/сn';
import { TASK_REMINDER_OPTIONS } from '../../../../consts/client-detail';
import { tasksSelectOverlayStyles } from './tasksSelectOverlayStyles';
import ChevronDownIcon from '/public/icons/chevron-down.svg';

type RemOpt = SelectorOption<Nullable<ReminderDayType>>;

const ReminderOptionRow = (props: OptionProps<RemOpt, false, GroupBase<RemOpt>>) => {
  const { innerProps, data, isSelected, isFocused } = props;
  const isActive = isSelected || isFocused;
  return (
    <div style={{ padding: '1px 6px' }}>
      <div
        {...innerProps}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          paddingTop: '8px',
          paddingBottom: '8px',
          paddingLeft: '8px',
          paddingRight: '10px',
          borderRadius: '6px',
          cursor: 'pointer',
          background: isActive ? '#F2F6FC' : 'transparent',
        }}
      >
        <span
          className={cn(
            'font-inter font-normal text-body-responsive',
            isActive ? 'text-black' : 'text-grey-700'
          )}
        >
          {data.label}
        </span>
      </div>
    </div>
  );
};

const ReminderSingleValue = (props: SingleValueProps<RemOpt, false, GroupBase<RemOpt>>) => {
  const { data, innerProps } = props;
  const isNeutral = data.value === null;
  return (
    <div
      {...innerProps}
      style={{
        gridArea: '1 / 1 / 2 / 3',
        maxWidth: '100%',
        overflow: 'hidden',
      }}
    >
      <span
        className={cn(
          'block overflow-hidden text-ellipsis whitespace-nowrap font-inter font-normal text-body-responsive',
          isNeutral ? 'text-grey-700' : 'text-[#212529]'
        )}
      >
        {data.label}
      </span>
    </div>
  );
};

interface TaskReminderSelectorProps {
  value: Nullable<ReminderDayType>;
  onChange: (next: Nullable<ReminderDayType>) => void;
  label?: string;
  name?: string;
  errors?: FieldErrors;
}

export const TaskReminderSelector = ({
  value,
  onChange,
  label = 'Remind me on',
  name = 'reminder_days',
  errors,
}: TaskReminderSelectorProps) => (
  <Selector<ReminderDayType>
    label={label}
    name={name}
    errors={errors}
    options={TASK_REMINDER_OPTIONS}
    value={value}
    option={TASK_REMINDER_OPTIONS.find((o) => o.value === value) ?? TASK_REMINDER_OPTIONS[0]}
    onChange={(opt: SingleValue<SelectorOption<Nullable<ReminderDayType>>>) => {
      onChange(opt?.value ?? null);
    }}
    isClearOption={false}
    isSearchable
    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
    menuPosition="fixed"
    customStyles={tasksSelectOverlayStyles<SelectorOption<Nullable<ReminderDayType>>>()}
    components={{
      Option: ReminderOptionRow as never,
      SingleValue: ReminderSingleValue as never,
      DropdownIndicator: (props) => (
        <ChevronDownIcon
          className={`transition-transform duration-200 text-black fill-current w-4 h-4 ${
            props.selectProps.menuIsOpen ? 'rotate-180' : ''
          }`}
        />
      ),
    }}
  />
);
